import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../../utils/api';
import { getDemoSandboxUrl, getEmbedUrl, getLocalEmbedHostUrl, isLocalEmbedTestingHost, joinEmbedSnippetUrl } from '../../../utils/config';
import { setDemoPublished } from '../../../utils/demoSandbox';
import { buildEmbedSnippets } from '../../../utils/embedSnippet';
import { useToolAccess } from '../../../tools/ToolAccessContext';

/**
 * EmbedSettings component - Settings form and embed code snippets for deployment
 * @param {Object} props - Component props
 */
const EmbedSettings = ({
    selectedDeployment,
    projectId,
    releases,
    guiSchemas = [],
    isActive,
    isSubmitting,
    error,
    successMsg,
    editName,
    setEditName,
    editGuiVersionTag,
    setEditGuiVersionTag,
    allowedOrigins,
    setAllowedOrigins,
    editReleaseId,
    setEditReleaseId,
    expiresAt,
    setExpiresAt,
    onToggleActive,
    onSaveSettings,
    copiedToken,
    setCopiedToken,
    copiedSnippet,
    setCopiedSnippet,
    copiedWidget,
    setCopiedWidget,
    activeEmbedTab,
    setActiveEmbedTab,
    onDeploymentUpdated
}) => {
    const embedBaseUrl = getEmbedUrl();
    const embedFrameUrl = selectedDeployment?.embedToken
        ? joinEmbedSnippetUrl(embedBaseUrl, selectedDeployment.embedToken)
        : '';
    const layoutVersionTag = editGuiVersionTag || selectedDeployment?.guiVersionTag || '';
    const [embedAppearance, setEmbedAppearance] = useState(null);

    useEffect(() => {
        if (!projectId || !layoutVersionTag) {
            setEmbedAppearance(null);
            return undefined;
        }

        let cancelled = false;

        const loadEmbedAppearance = async () => {
            try {
                const res = await apiFetch(`/api/projects/${projectId}/gui-schema?versionTag=${encodeURIComponent(layoutVersionTag)}`);
                if (!res.ok || cancelled) return;
                const schema = await res.json();
                const payload = schema.designPayload || schema.DesignPayload || {};
                const properties = payload.properties || payload.Properties || {};
                if (!cancelled) {
                    setEmbedAppearance(properties.embed || properties.Embed || null);
                }
            } catch (err) {
                console.error('Failed to load GUI embed settings', err);
                if (!cancelled) setEmbedAppearance(null);
            }
        };

        loadEmbedAppearance();
        return () => {
            cancelled = true;
        };
    }, [projectId, layoutVersionTag]);

    const snippets = useMemo(
        () => buildEmbedSnippets({
            token: selectedDeployment?.embedToken,
            embedBaseUrl,
            embedFrameUrl,
            embed: embedAppearance
        }),
        [selectedDeployment?.embedToken, embedBaseUrl, embedFrameUrl, embedAppearance]
    );

    const handleCopyToken = () => {
        if (!selectedDeployment?.embedToken) return;
        navigator.clipboard.writeText(selectedDeployment.embedToken);
        setCopiedToken(true);
        setTimeout(() => setCopiedToken(false), 2000);
    };

    const handleCopySnippet = () => {
        if (!snippets.iframe) return;
        navigator.clipboard.writeText(snippets.iframe);
        setCopiedSnippet(true);
        setTimeout(() => setCopiedSnippet(false), 2000);
    };

    const handleCopyWidget = () => {
        if (!snippets.widget) return;
        navigator.clipboard.writeText(snippets.widget);
        setCopiedWidget(true);
        setTimeout(() => setCopiedWidget(false), 2000);
    };

    return (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }} className="fade-in">
            <SettingsForm
                selectedDeployment={selectedDeployment}
                releases={releases}
                guiSchemas={guiSchemas}
                isActive={isActive}
                isSubmitting={isSubmitting}
                error={error}
                successMsg={successMsg}
                editName={editName}
                setEditName={setEditName}
                editGuiVersionTag={editGuiVersionTag}
                setEditGuiVersionTag={setEditGuiVersionTag}
                allowedOrigins={allowedOrigins}
                setAllowedOrigins={setAllowedOrigins}
                editReleaseId={editReleaseId}
                setEditReleaseId={setEditReleaseId}
                expiresAt={expiresAt}
                setExpiresAt={setExpiresAt}
                onToggleActive={onToggleActive}
                onSaveSettings={onSaveSettings}
            />

            <div style={{ flex: 4.5, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <EmbedSnippets
                    selectedDeployment={selectedDeployment}
                    isActive={isActive}
                    iframeSnippet={snippets.iframe}
                    widgetSnippet={snippets.widget}
                    copiedToken={copiedToken}
                    setCopiedToken={setCopiedToken}
                    copiedSnippet={copiedSnippet}
                    setCopiedSnippet={setCopiedSnippet}
                    copiedWidget={copiedWidget}
                    setCopiedWidget={setCopiedWidget}
                    activeEmbedTab={activeEmbedTab}
                    setActiveEmbedTab={setActiveEmbedTab}
                    handleCopyToken={handleCopyToken}
                    handleCopySnippet={handleCopySnippet}
                    handleCopyWidget={handleCopyWidget}
                />
                <DemoAccessPanel
                    selectedDeployment={selectedDeployment}
                    onDeploymentUpdated={onDeploymentUpdated}
                />
            </div>
        </div>
    );
};

/**
 * Settings form for deployment configuration
 */
const SettingsForm = ({
    selectedDeployment,
    releases,
    guiSchemas = [],
    isActive,
    isSubmitting,
    error,
    successMsg,
    editName,
    setEditName,
    editGuiVersionTag,
    setEditGuiVersionTag,
    allowedOrigins,
    setAllowedOrigins,
    editReleaseId,
    setEditReleaseId,
    expiresAt,
    setExpiresAt,
    onToggleActive,
    onSaveSettings
}) => {
    const savedTags = guiSchemas.map(schema => schema.versionTag);
    const currentTag = editGuiVersionTag || selectedDeployment?.guiVersionTag || '';
    const layoutVersionOptions = currentTag && !savedTags.includes(currentTag)
        ? [{ versionTag: currentTag, isPublished: false }, ...guiSchemas]
        : guiSchemas;

    return (
    <div style={{ 
        flex: 3.5, 
        background: 'var(--bg-secondary)', 
        padding: '1rem', 
        border: '1px solid var(--border)', 
        borderRadius: 'var(--radius)', 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '1.25rem' 
    }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
            Deployment Details
        </h3>

        {error && (
            <ErrorMessage message={error} />
        )}
        {successMsg && (
            <SuccessMessage message={successMsg} />
        )}

        <form onSubmit={onSaveSettings} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <FormField
                label="Deployment Name"
                required
            >
                <input
                    type="text"
                    className="input-field"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    style={{ background: 'var(--bg-primary)' }}
                    required
                />
            </FormField>

            <FormField label="Rule Release">
                {releases.length === 0 ? (
                    <ErrorMessage message="No releases found. Create a deployment or publish a release in Version Control." />
                ) : (
                    <select
                        className="input-field"
                        value={editReleaseId}
                        onChange={e => setEditReleaseId(e.target.value)}
                        style={{ background: 'var(--bg-primary)', width: '100%', cursor: 'pointer' }}
                        required
                    >
                        {releases.map(release => (
                            <option key={release.id} value={release.id.toString()}>
                                {release.name} ({release.isNonProduction ? 'Test Release' : 'Production Release'})
                            </option>
                        ))}
                    </select>
                )}
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                    Maps this deployment to a published rule configuration snapshot.
                </p>
            </FormField>

            <FormField label="Layout Version">
                {layoutVersionOptions.length === 0 ? (
                    <ErrorMessage message="No layout versions found. Save a layout in the GUI Designer to create a version tag." />
                ) : (
                    <select
                        className="input-field"
                        value={editGuiVersionTag}
                        onChange={e => setEditGuiVersionTag(e.target.value)}
                        style={{ background: 'var(--bg-primary)', width: '100%', cursor: 'pointer' }}
                        required
                    >
                        {layoutVersionOptions.map(schema => (
                            <option key={schema.versionTag} value={schema.versionTag}>
                                {schema.versionTag}{schema.isPublished ? ' (Published)' : ''}
                            </option>
                        ))}
                    </select>
                )}
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                    {guiSchemas.length === 0
                        ? 'Save a layout in the GUI Designer to create a version tag.'
                        : 'Selects which saved GUI layout the embed serves for this deployment.'}
                </p>
            </FormField>

            <FormField label="Whitelisted Domains (CORS Origins)">
                <input
                    type="text"
                    className="input-field"
                    placeholder="e.g. http://localhost:3000, https://app.company.com"
                    value={allowedOrigins}
                    onChange={e => setAllowedOrigins(e.target.value)}
                    style={{ background: 'var(--bg-primary)' }}
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                    Comma-separated origins whitelisted for evaluation. A blank list denies every origin. Use * to allow any origin.
                    {isLocalEmbedTestingHost() && ` For the local sample host, whitelist ${window.location.protocol}//${window.location.hostname}:8088.`}
                </p>
            </FormField>

            <FormField label="Expiration Date">
                <input
                    type="date"
                    className="input-field"
                    value={expiresAt}
                    onChange={e => setExpiresAt(e.target.value)}
                    style={{ background: 'var(--bg-primary)' }}
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                    Optional expiration limit. Configurator access is locked after this date.
                </p>
            </FormField>

            <ActionBar
                isActive={isActive}
                onToggleActive={onToggleActive}
                isSubmitting={isSubmitting}
            />
        </form>
    </div>
    );
};

/**
 * Form field wrapper
 */
const FormField = ({ label, required, children }) => (
    <div>
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
            {label}
            {required && <span style={{ color: 'var(--error)', marginLeft: '0.25rem' }}>*</span>}
        </label>
        {children}
    </div>
);

/**
 * Action bar with toggle and save buttons
 */
const ActionBar = ({ isActive, onToggleActive, isSubmitting }) => {
    const { canEditCurrent } = useToolAccess();
    return (
    <div style={{ display: 'flex', gap: '1rem', borderTop: '1px solid var(--border)', paddingTop: '1.25rem', marginTop: '0.5rem' }}>
        <button
            type="button"
            onClick={onToggleActive}
            className="btn-secondary"
            disabled={!canEditCurrent}
            style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '0.35rem', 
                borderColor: isActive ? 'var(--error)' : 'var(--success)', 
                color: isActive ? 'var(--error)' : 'var(--success)' 
            }}
        >
            <i className={`fa-solid ${isActive ? 'fa-power-off' : 'fa-play'}`}></i>
            {isActive ? 'Revoke Access' : 'Activate Access'}
        </button>

        <button
            type="submit"
            className="btn-primary"
            style={{ marginLeft: 'auto', background: 'var(--text-primary)', color: 'var(--bg-primary)' }}
            disabled={isSubmitting || !canEditCurrent}
        >
            {isSubmitting ? 'Saving...' : 'Save Configuration'}
        </button>
    </div>
    );
};

/**
 * Error message display
 */
const ErrorMessage = ({ message }) => (
    <div style={{ 
        padding: '0.75rem 1rem', 
        background: 'rgba(220,38,38,0.08)', 
        border: '1px solid var(--error)', 
        borderRadius: 'var(--radius)', 
        color: 'var(--error)', 
        fontSize: '0.85rem' 
    }}>
        {message}
    </div>
);

/**
 * Success message display
 */
const SuccessMessage = ({ message }) => (
    <div style={{ 
        padding: '0.75rem 1rem', 
        background: 'rgba(5,150,105,0.08)', 
        border: '1px solid var(--success)', 
        borderRadius: 'var(--radius)', 
        color: 'var(--success)', 
        fontSize: '0.85rem' 
    }}>
        {message}
    </div>
);

/**
 * Embed snippets panel
 */
const EmbedSnippets = ({
    selectedDeployment,
    isActive,
    iframeSnippet,
    widgetSnippet,
    copiedToken,
    setCopiedToken,
    copiedSnippet,
    setCopiedSnippet,
    copiedWidget,
    setCopiedWidget,
    activeEmbedTab,
    setActiveEmbedTab,
    handleCopyToken,
    handleCopySnippet,
    handleCopyWidget
}) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <SnippetSection
            selectedDeployment={selectedDeployment}
            isActive={isActive}
            copiedToken={copiedToken}
            setCopiedToken={setCopiedToken}
            handleCopyToken={handleCopyToken}
        />

        <CodeSnippets
            iframeSnippet={iframeSnippet}
            widgetSnippet={widgetSnippet}
            copiedSnippet={copiedSnippet}
            setCopiedSnippet={setCopiedSnippet}
            copiedWidget={copiedWidget}
            setCopiedWidget={setCopiedWidget}
            activeEmbedTab={activeEmbedTab}
            setActiveEmbedTab={setActiveEmbedTab}
            handleCopySnippet={handleCopySnippet}
            handleCopyWidget={handleCopyWidget}
        />
    </div>
);

/**
 * Token snippet section
 */
const SnippetSection = ({ selectedDeployment, isActive, copiedToken, handleCopyToken }) => (
    <div style={{ 
        background: 'var(--bg-card)', 
        padding: '1rem', 
        border: '1px solid var(--border)', 
        borderRadius: 'var(--radius)', 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '1.25rem' 
    }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
                Embedding Snippets
            </h3>
            <StatusIndicator isActive={isActive} />
        </div>

        <TokenField
            selectedDeployment={selectedDeployment}
            copiedToken={copiedToken}
            handleCopyToken={handleCopyToken}
        />

        <LocalEmbedActions
            selectedDeployment={selectedDeployment}
        />
    </div>
);

const DemoAccessPanel = ({ selectedDeployment, onDeploymentUpdated }) => {
    const [nextPassword, setNextPassword] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [panelError, setPanelError] = useState(null);
    const [panelSuccess, setPanelSuccess] = useState(null);
    const [copiedLink, setCopiedLink] = useState(false);
    const [copiedPassword, setCopiedPassword] = useState(false);

    const demoUrl = selectedDeployment?.embedToken ? getDemoSandboxUrl(selectedDeployment.embedToken) : '';
    const currentPassword = selectedDeployment?.demoPassword || '';
    const isProtected = Boolean(selectedDeployment?.hasDemoPassword || currentPassword);
    const isPublished = Boolean(selectedDeployment?.isDemoPublished);

    const copyValue = async (value, setCopied) => {
        if (!value) return;
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const savePassword = async (password) => {
        if (!selectedDeployment?.id) return;
        setIsSaving(true);
        setPanelError(null);
        setPanelSuccess(null);
        try {
            const res = await apiFetch(`/api/deployments/${selectedDeployment.id}/demo-password`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password })
            });
            if (!res.ok) {
                const text = await res.text();
                setPanelError(text || 'Failed to update demo password.');
                return;
            }
            const updated = await res.json();
            onDeploymentUpdated?.(updated);
            setNextPassword('');
            setPanelSuccess(password ? 'Demo password saved.' : 'Demo password removed.');
        } catch (err) {
            console.error(err);
            setPanelError('Could not update demo password.');
        } finally {
            setIsSaving(false);
        }
    };

    const updatePublished = async (published) => {
        if (!selectedDeployment?.id) return;
        setIsPublishing(true);
        setPanelError(null);
        setPanelSuccess(null);
        try {
            const updated = await setDemoPublished(selectedDeployment.id, published);
            onDeploymentUpdated?.(updated);
            setPanelSuccess(published ? 'Demo page published.' : 'Demo page retracted.');
        } catch (err) {
            console.error(err);
            setPanelError(err.message || 'Could not update demo page visibility.');
        } finally {
            setIsPublishing(false);
        }
    };

    const buttonStyle = {
        padding: '0.5rem 0.85rem',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem'
    };

    return (
        <div style={{
            background: 'var(--bg-card)',
            padding: '1rem',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.85rem'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>Demo sandbox</h4>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: isPublished ? 'var(--success)' : 'var(--text-secondary)' }}>
                    {isPublished ? 'Published' : 'Not published'}
                </span>
            </div>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                Publish to make this hosted `/demo` link work for guests. Retract turns off the demo page only; iframe and widget embeds stay live. If a password is set, guests must enter it before the configurator loads.
            </p>

            {panelError && <ErrorMessage message={panelError} />}
            {panelSuccess && <SuccessMessage message={panelSuccess} />}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <button
                    type="button"
                    className="btn-primary"
                    style={{ ...buttonStyle, background: 'var(--text-primary)', color: 'var(--bg-primary)' }}
                    disabled={isPublishing || isPublished}
                    onClick={() => updatePublished(true)}
                >
                    <i className="fa-solid fa-upload"></i>
                    {isPublishing && !isPublished ? 'Publishing...' : 'Publish to demo page'}
                </button>
                <button
                    type="button"
                    className="btn-secondary"
                    style={{ ...buttonStyle, borderColor: 'var(--error)', color: 'var(--error)' }}
                    disabled={isPublishing || !isPublished}
                    onClick={() => updatePublished(false)}
                >
                    <i className="fa-solid fa-rotate-left"></i>
                    {isPublishing && isPublished ? 'Retracting...' : 'Retract demo page'}
                </button>
            </div>

            <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    Demo link
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <input
                        type="text"
                        className="input-field"
                        value={demoUrl}
                        readOnly
                        style={{ fontFamily: 'monospace', fontSize: '0.8rem', padding: '0.5rem 0.75rem', flex: 1, minWidth: '180px' }}
                    />
                    <button
                        type="button"
                        className="btn-secondary"
                        style={{ ...buttonStyle, background: copiedLink ? 'var(--success)' : 'transparent', color: copiedLink ? 'white' : 'var(--text-secondary)' }}
                        onClick={() => copyValue(demoUrl, setCopiedLink)}
                    >
                        {copiedLink ? <><i className="fa-solid fa-check"></i> Copied</> : <><i className="fa-regular fa-copy"></i> Copy link</>}
                    </button>
                    <button
                        type="button"
                        className="btn-secondary"
                        style={buttonStyle}
                        onClick={() => window.open(demoUrl, '_blank', 'noopener,noreferrer')}
                        disabled={!demoUrl}
                    >
                        <i className="fa-solid fa-arrow-up-right-from-square"></i>
                        Open
                    </button>
                </div>
            </div>

            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: isProtected ? 'var(--success)' : 'var(--text-secondary)' }}>
                {isProtected ? 'Password protected' : 'Anyone with the published link'}
            </div>

            {isProtected && (
                <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                        Current password
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                            type="text"
                            className="input-field"
                            value={currentPassword}
                            readOnly
                            style={{ fontFamily: 'monospace', fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
                        />
                        <button
                            type="button"
                            className="btn-secondary"
                            style={{ ...buttonStyle, background: copiedPassword ? 'var(--success)' : 'transparent', color: copiedPassword ? 'white' : 'var(--text-secondary)' }}
                            onClick={() => copyValue(currentPassword, setCopiedPassword)}
                            disabled={!currentPassword}
                        >
                            {copiedPassword ? <><i className="fa-solid fa-check"></i> Copied</> : <><i className="fa-regular fa-copy"></i> Copy password</>}
                        </button>
                    </div>
                </div>
            )}

            <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    {isProtected ? 'Change password' : 'Set password'}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <input
                        type="text"
                        className="input-field"
                        value={nextPassword}
                        onChange={e => setNextPassword(e.target.value)}
                        placeholder={isProtected ? 'New password' : 'Optional guest password'}
                        style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem', flex: 1, minWidth: '160px' }}
                        autoComplete="off"
                    />
                    <button
                        type="button"
                        className="btn-primary"
                        style={{ ...buttonStyle, background: 'var(--text-primary)', color: 'var(--bg-primary)' }}
                        disabled={isSaving || !nextPassword.trim()}
                        onClick={() => savePassword(nextPassword.trim())}
                    >
                        {isSaving ? 'Saving...' : 'Save password'}
                    </button>
                    {isProtected && (
                        <button
                            type="button"
                            className="btn-secondary"
                            style={buttonStyle}
                            disabled={isSaving}
                            onClick={() => savePassword(null)}
                        >
                            Remove password
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const LocalEmbedActions = ({ selectedDeployment }) => {
    if (!isLocalEmbedTestingHost() || !selectedDeployment?.embedToken) return null;

    const sampleHostUrl = getLocalEmbedHostUrl(selectedDeployment.embedToken);
    const buttonStyle = {
        padding: '0.5rem 0.85rem',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem'
    };

    return (
        <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <button
                    type="button"
                    className="btn-secondary"
                    style={buttonStyle}
                    onClick={() => window.open(sampleHostUrl, '_blank', 'noopener,noreferrer')}
                >
                    <i className="fa-solid fa-window-restore"></i>
                    Test on sample host
                </button>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0.5rem 0 0' }}>
                Sample host opens localhost:8088 so Allowed Origins is exercised for iframe embeds.
            </p>
        </div>
    );
};

/**
 * Status indicator
 */
const StatusIndicator = ({ isActive }) => (
    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: isActive ? 'var(--success)' : 'var(--error)' }}>
        {isActive ? '● Live integration' : '○ Disabled'}
    </span>
);

/**
 * Token field with copy button
 */
const TokenField = ({ selectedDeployment, copiedToken, handleCopyToken }) => (
    <div>
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            Embed Token
        </label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
                type="text"
                className="input-field"
                value={selectedDeployment.embedToken}
                readOnly
                style={{ fontFamily: 'monospace', fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}
            />
            <button
                onClick={handleCopyToken}
                className="btn-secondary"
                style={{ 
                    padding: '0.5rem 1rem', 
                    whiteSpace: 'nowrap', 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '0.25rem', 
                    background: copiedToken ? 'var(--success)' : 'transparent', 
                    color: copiedToken ? 'white' : 'var(--text-secondary)' 
                }}
            >
                {copiedToken ? <><i className="fa-solid fa-check"></i> Copied</> : <><i className="fa-regular fa-copy"></i> Copy</>}
            </button>
        </div>
    </div>
);

/**
 * Code snippets with tabs
 */
const CodeSnippets = ({
    iframeSnippet,
    widgetSnippet,
    copiedSnippet,
    setCopiedSnippet,
    copiedWidget,
    setCopiedWidget,
    activeEmbedTab,
    setActiveEmbedTab,
    handleCopySnippet,
    handleCopyWidget
}) => (
    <div style={{ background: 'var(--bg-card)', padding: '1rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <CodeTabs activeTab={activeEmbedTab} setActiveTab={setActiveEmbedTab} />

        {activeEmbedTab === 'iframe' ? (
            <IframeSnippet
                snippet={iframeSnippet}
                copiedSnippet={copiedSnippet}
                setCopiedSnippet={setCopiedSnippet}
                handleCopySnippet={handleCopySnippet}
            />
        ) : (
            <WidgetSnippet
                snippet={widgetSnippet}
                copiedWidget={copiedWidget}
                setCopiedWidget={setCopiedWidget}
                handleCopyWidget={handleCopyWidget}
            />
        )}
    </div>
);

/**
 * Code tabs for snippet types
 */
const CodeTabs = ({ activeTab, setActiveTab }) => (
    <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.25rem', marginBottom: '1rem' }}>
        <TabButton
            label="Standard iframe"
            active={activeTab === 'iframe'}
            onClick={() => setActiveTab('iframe')}
        />
        <TabButton
            label="Dynamic JS Widget"
            active={activeTab === 'widget'}
            onClick={() => setActiveTab('widget')}
        />
    </div>
);

/**
 * Tab button for code tabs
 */
const TabButton = ({ label, active, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            background: 'transparent',
            border: 'none',
            borderBottom: active ? '2px solid var(--text-primary)' : '2px solid transparent',
            color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
            fontWeight: active ? 600 : 400,
            padding: '0.25rem 0.5rem',
            cursor: 'pointer',
            fontSize: '0.85rem'
        }}
    >
        {label}
    </button>
);

/**
 * Iframe snippet with copy button
 */
const IframeSnippet = ({ snippet, copiedSnippet, handleCopySnippet }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <textarea
            className="textarea-field"
            readOnly
            value={snippet}
            style={{ 
                fontFamily: 'monospace', 
                fontSize: '0.8rem', 
                minHeight: '60px', 
                background: 'var(--bg-secondary)', 
                color: 'var(--text-primary)', 
                padding: '0.5rem' 
            }}
        />
        <button
            type="button"
            onClick={handleCopySnippet}
            className="btn-primary"
            style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '0.25rem', 
                alignSelf: 'flex-end', 
                fontSize: '0.8rem', 
                padding: '0.4rem 0.8rem', 
                background: copiedSnippet ? 'var(--success)' : 'var(--text-primary)', 
                color: copiedSnippet ? 'white' : 'var(--bg-primary)' 
            }}
        >
            {copiedSnippet ? <><i className="fa-solid fa-check"></i> Copied!</> : <><i className="fa-regular fa-copy"></i> Copy iframe code</>}
        </button>
    </div>
);

/**
 * Widget snippet with copy button
 */
const WidgetSnippet = ({ snippet, copiedWidget, handleCopyWidget }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <textarea
            className="textarea-field"
            readOnly
            value={snippet}
            style={{ 
                fontFamily: 'monospace', 
                fontSize: '0.8rem', 
                minHeight: '120px', 
                background: 'var(--bg-secondary)', 
                color: 'var(--text-primary)', 
                lineHeight: '1.4', 
                padding: '0.5rem' 
            }}
        />
        <button
            type="button"
            onClick={handleCopyWidget}
            className="btn-primary"
            style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '0.25rem', 
                alignSelf: 'flex-end', 
                fontSize: '0.8rem', 
                padding: '0.4rem 0.8rem', 
                background: copiedWidget ? 'var(--success)' : 'var(--text-primary)', 
                color: copiedWidget ? 'white' : 'var(--bg-primary)' 
            }}
        >
            {copiedWidget ? <><i className="fa-solid fa-check"></i> Copied!</> : <><i className="fa-regular fa-copy"></i> Copy Widget Code</>}
        </button>
    </div>
);

export default EmbedSettings;
export { 
    SettingsForm,
    FormField,
    ActionBar,
    ErrorMessage,
    SuccessMessage,
    EmbedSnippets,
    SnippetSection,
    StatusIndicator,
    TokenField,
    LocalEmbedActions,
    CodeSnippets,
    CodeTabs,
    TabButton,
    IframeSnippet,
    WidgetSnippet
};
